/**
 * SmartChoicesCard.js
 * Displays cumulative calorie savings from smart combo swaps
 * 
 * Shows in MIQ View for Genius Plaite users:
 * - Total calories saved (all time)
 * - This month's savings
 * - Smart swap streak
 * - Fun comparison (e.g., "a Big Mac's worth")
 */

import React, { useState, useEffect } from 'react';
import { Award, TrendingUp, Flame, Leaf, ChevronDown, ChevronUp } from 'lucide-react';
import { isGeniusTier } from '../../utils/tierUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const SmartChoicesCard = ({ userId, userTier, onShowToast }) => {
  const [savingsStats, setSavingsStats] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Check if user has Genius access - using centralized utility
  const isGeniusPlaite = isGeniusTier(userTier);
  
  useEffect(() => {
    if (userId && isGeniusPlaite) {
      setIsLoading(true);
      fetch(`${BACKEND_URL}/api/smart-picks/smart-savings/${userId}`)
        .then(res => res.json())
        .then(data => {
          setSavingsStats(data);
          setIsLoading(false);
        })
        .catch(err => {
          console.error('Failed to fetch savings stats:', err);
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, [userId, isGeniusPlaite]);
  
  // Don't show if not Genius tier or no savings yet
  if (!isGeniusPlaite) return null;
  if (isLoading) return null;
  if (!savingsStats || savingsStats.total_calories_saved === 0) return null;
  
  const { 
    total_calories_saved, 
    total_swaps, 
    this_month_calories, 
    this_month_swaps,
    this_week_calories,
    this_week_swaps,
    streak_days,
    comparison,
    monthly_comparison 
  } = savingsStats;
  
  return (
    <div 
      data-testid="smart-choices-card"
      style={{
        background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.12) 0%, rgba(139, 195, 74, 0.08) 100%)',
        border: '1px solid rgba(76, 175, 80, 0.25)',
        borderRadius: '16px',
        padding: '16px',
        marginBottom: '16px'
      }}
    >
      {/* Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        data-testid="smart-choices-toggle"
        style={{
          width: '100%',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            background: 'rgba(76, 175, 80, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Leaf size={20} style={{ color: '#4CAF50' }} />
          </div>
          <div style={{ textAlign: 'left' }}>
            <div style={{ 
              fontSize: '14px', 
              fontWeight: '600', 
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              Smart Choices Tracker
              <span style={{
                fontSize: '9px',
                padding: '2px 6px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: '4px',
                fontWeight: '600'
              }}>
                GENIUS
              </span>
            </div>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>
              {total_calories_saved.toLocaleString()} calories saved all time
            </div>
          </div>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {streak_days > 0 && (
            <div style={{
              padding: '4px 8px',
              background: 'rgba(255, 152, 0, 0.2)',
              border: '1px solid rgba(255, 152, 0, 0.4)',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: '600',
              color: '#FF9800',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <Flame size={12} /> {streak_days}
            </div>
          )}
          {isExpanded ? (
            <ChevronUp size={18} style={{ color: 'rgba(255,255,255,0.5)' }} />
          ) : (
            <ChevronDown size={18} style={{ color: 'rgba(255,255,255,0.5)' }} />
          )}
        </div>
      </button>
      
      {/* Expanded Content */}
      {isExpanded && (
        <div style={{ marginTop: '16px' }}>
          {/* Stats Grid */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: '10px',
            marginBottom: '12px'
          }}>
            {/* This Week */}
            <div style={{
              padding: '12px',
              background: 'rgba(76, 175, 80, 0.1)',
              borderRadius: '10px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#4CAF50' }}>
                {this_week_calories.toLocaleString()}
              </div>
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>
                cal this week
              </div>
              <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.4)' }}>
                {this_week_swaps} swap{this_week_swaps !== 1 ? 's' : ''}
              </div>
            </div>
            
            {/* This Month */}
            <div style={{
              padding: '12px',
              background: 'rgba(102, 126, 234, 0.1)',
              borderRadius: '10px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#667eea' }}>
                {this_month_calories.toLocaleString()}
              </div>
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>
                cal this month
              </div>
              <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.4)' }}>
                {this_month_swaps} swap{this_month_swaps !== 1 ? 's' : ''}
              </div>
            </div>
            
            {/* All Time */}
            <div style={{
              padding: '12px',
              background: 'rgba(156, 39, 176, 0.1)',
              borderRadius: '10px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#9C27B0' }}>
                {total_calories_saved.toLocaleString()}
              </div>
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>
                cal all time
              </div>
              <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.4)' }}>
                {total_swaps} swap{total_swaps !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
          
          {/* Fun Comparison */}
          {comparison && comparison.text && (
            <div style={{
              padding: '12px',
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '10px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', marginBottom: '4px' }}>
                {comparison.emoji}
              </div>
              <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)', fontWeight: '500' }}>
                You've avoided {comparison.text} of calories!
              </div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>
                Keep making smart choices with Smart Swaps
              </div>
            </div>
          )}
          
          {/* Streak Info */}
          {streak_days > 0 && (
            <div style={{
              marginTop: '12px',
              padding: '10px',
              background: 'rgba(255, 152, 0, 0.1)',
              border: '1px solid rgba(255, 152, 0, 0.2)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <Flame size={16} style={{ color: '#FF9800' }} />
              <span style={{ fontSize: '12px', color: '#FF9800', fontWeight: '500' }}>
                {streak_days} day streak! Keep making smart choices!
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SmartChoicesCard;
